import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Headphones, Sparkles } from 'lucide-react';
import ProductGrid from '../components/ProductGrid';
import CategoryNav from '../components/CategoryNav';
import { Product } from '../components/ProductCard';
import { useVoice } from '../contexts/voice';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import Pagination from '../components/ui/Pagination';
import { cn } from '../lib/utils';

const PRODUCTS_PER_PAGE = 40;

interface Category {
  id: string;
  name: string;
  count: number;
}

interface Subcategory {
  id: string;
  name: string;
  count: number;
}

export default function StorePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('rating');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const { highlightedProductIds, sendContext, setSelectedProduct } = useVoice();
  const { getCartCount, items: cartItems } = useCart();
  const { theme } = useTheme();
  const { getAuthHeaders } = useUser();
  const { language, t } = useLanguage();

  const searchQuery = searchParams.get('search') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const subcategoryFromUrl = searchParams.get('subcategory');

  useEffect(() => {
    if (pageParam !== currentPage && pageParam >= 1) {
      setCurrentPage(pageParam);
    }
  }, [pageParam]);

  const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = products.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);

  const categoryFromUrl = searchParams.get('category');
  useEffect(() => {
    if (categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
      setCurrentPage(1);
    }
  }, [categoryFromUrl]);

  useEffect(() => {
    if (subcategoryFromUrl !== selectedSubcategory) {
      setSelectedSubcategory(subcategoryFromUrl);
      setCurrentPage(1);
    }
  }, [subcategoryFromUrl]);

  const handleCategorySelect = (category: string | null) => {
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }

    sendContext({
      page: category ? 'categorie' : 'home',
      category: category || undefined,
      cart_count: getCartCount(),
      cart_items: cartItems.map(i => ({
        id: i.product.id,
        name: i.product.name,
        price: i.product.price,
        quantity: i.quantity
      })),
      visible_products: ['Producten worden geladen...'],
      available_actions: ['Wachten tot producten geladen zijn...'],
    });
  };

  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages || 1));
    setCurrentPage(validPage);

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (validPage === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', validPage.toString());
      }
      return newParams;
    }, { replace: true });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/categories?lang=${language}`, { headers });
        const data = await res.json();
        setCategories(data.categories || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCategories();
  }, [getAuthHeaders, language]);

  useEffect(() => {
    if (selectedCategory) {
      const fetchSubcategories = async () => {
        try {
          const headers = await getAuthHeaders();
          const res = await fetch(`/api/subcategories/${selectedCategory}?lang=${language}`, { headers });
          const data = await res.json();
          setSubcategories(data.subcategories || []);
        } catch (err) {
          console.error(err);
        }
      };
      fetchSubcategories();
    } else {
      setSubcategories([]);
    }
  }, [selectedCategory, getAuthHeaders, language]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      let url = '/api/products?limit=2000';

      if (searchQuery) {
        url = `/api/products/search?q=${encodeURIComponent(searchQuery)}&limit=500`;
      } else if (selectedCategory && selectedSubcategory) {
        url = `/api/products?category=${selectedCategory}&subcategory=${selectedSubcategory}&limit=1000&sort=${sortBy}`;
      } else if (selectedCategory) {
        url = `/api/products?category=${selectedCategory}&limit=1000&sort=${sortBy}`;
      }

      try {
        const headers = await getAuthHeaders();
        const res = await fetch(url, { headers });
        const data = await res.json();
        let prods = data.products || [];

        if (sortBy === 'price_asc') {
          prods = [...prods].sort((a: Product, b: Product) => a.price - b.price);
        } else if (sortBy === 'price_desc') {
          prods = [...prods].sort((a: Product, b: Product) => b.price - a.price);
        } else if (sortBy === 'rating') {
          prods = [...prods].sort((a: Product, b: Product) => (b.rating || 0) - (a.rating || 0));
        }

        setProducts(prods);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory, selectedSubcategory, searchQuery, sortBy, getAuthHeaders]);

  useEffect(() => {
    const cartTotal = cartItems.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

    sendContext({
      page: searchQuery ? 'zoekresultaten' : selectedCategory ? 'categorie' : 'home',
      category: selectedCategory || undefined,
      subcategory: selectedSubcategory || undefined,
      search_query: searchQuery || undefined,
      sort_by: sortBy,
      available_subcategories: selectedCategory && subcategories.length > 0
        ? subcategories.map(s => ({ id: s.id, name: s.name }))
        : undefined,
      pagination: {
        current_page: currentPage,
        total_pages: totalPages,
        products_per_page: PRODUCTS_PER_PAGE,
        total_products: products.length,
        showing: `${startIndex + 1}-${Math.min(startIndex + PRODUCTS_PER_PAGE, products.length)} van ${products.length}`,
      },
      cart_count: getCartCount(),
      cart_total: cartTotal,
      cart_items: cartItems.map(i => ({
        id: i.product.id,
        name: i.product.name,
        price: i.product.price,
        quantity: i.quantity
      })),
      visible_products: paginatedProducts.map(p =>
        `${p.id}: ${p.brand} ${p.name} - €${p.price} (${p.in_stock ? 'op voorraad' : 'niet op voorraad'})`
      ),
      highlighted_items: highlightedProductIds.length > 0
        ? paginatedProducts.filter(p => highlightedProductIds.includes(p.id)).map(p => `${p.id}: ${p.name}`)
        : undefined,
      available_actions: [
        'Producten zoeken',
        'Categorie selecteren: ' + categories.map(c => c.name).join(', '),
        'Sorteren: beste/goedkoopst/duurste',
        'Product toevoegen aan winkelwagen',
        'Naar winkelwagen gaan',
        'Naar bestellingen gaan',
        ...(currentPage > 1 ? ['Vorige pagina'] : []),
        ...(currentPage < totalPages ? ['Volgende pagina'] : []),
      ],
      navigation: [
        'Home',
        'Winkelwagen' + (getCartCount() > 0 ? ` (${getCartCount()} items)` : ''),
        'Bestellingen',
        ...categories.map(c => `Categorie: ${c.name}`),
      ],
    });
  }, [products, paginatedProducts, currentPage, totalPages, startIndex, selectedCategory, selectedSubcategory, subcategories, cartItems, sendContext, getCartCount, categories, searchQuery, highlightedProductIds]);

  const sortBtnClass = (active: boolean) => cn(
    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
    active
      ? 'bg-primary text-white'
      : 'bg-card border border-border hover:border-primary/40 text-foreground',
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Hero Section - Compact */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {theme.heroTitle ? (
                  <><span className="text-primary">{theme.name}</span> — {theme.heroTitle}</>
                ) : (
                  <>{t('store.welcome')} <span className="text-primary">{theme.name}</span> {t('store.voice_demo')}</>
                )}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {theme.heroSubtitle || t('store.hero_subtitle')}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-card/50 border border-primary/10">
                <Headphones className="w-3.5 h-3.5 text-primary" />
                {t('store.feature_voice')}
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-card/50 border border-primary/10">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                {t('store.feature_products')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-section="products">
        {/* Category Navigation */}
        <div className="mb-8" data-section="categories">
          <h2 className="text-xl font-bold text-foreground mb-4">{t('store.categories_title')}</h2>
          <CategoryNav
            categories={categories}
            selected={selectedCategory}
            onSelect={handleCategorySelect}
            allLabel={t('category.all')}
          />
        </div>

        {/* Subcategory Navigation */}
        {selectedCategory && subcategories.length > 0 && (
          <div className="mb-8" data-section="subcategories">
            <h3 className="text-lg font-semibold text-foreground mb-3">
              {t('store.subcategories_in', {
                category: categories.find(c => c.id === selectedCategory)?.name || selectedCategory
              })}
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSearchParams({ category: selectedCategory });
                }}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  !selectedSubcategory
                    ? 'bg-primary text-white'
                    : 'bg-card border border-border text-foreground hover:border-primary/40',
                )}
              >
                {t('store.all_in_category')} ({subcategories.reduce((sum, s) => sum + s.count, 0)})
              </button>
              {subcategories.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => {
                    setSearchParams({ category: selectedCategory, subcategory: sub.id });
                  }}
                  className={cn(
                    'px-4 py-2 rounded-full text-sm font-medium transition-all',
                    selectedSubcategory === sub.id
                      ? 'bg-primary text-white'
                      : 'bg-card border border-border text-foreground hover:border-primary/40',
                  )}
                >
                  {sub.name} ({sub.count})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Product Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground">
              {searchQuery
                ? `${t('store.results', { count: products.length })} "${searchQuery}"`
                : selectedCategory
                  ? t(`category.${selectedCategory}`) !== `category.${selectedCategory}`
                    ? t(`category.${selectedCategory}`)
                    : categories.find(c => c.id === selectedCategory)?.name || t('store.products_title')
                  : t('store.all_products')}
            </h2>
            <span className="text-sm text-muted-foreground">
              {t('pagination.of_products', { start: startIndex + 1, end: Math.min(startIndex + PRODUCTS_PER_PAGE, products.length), total: products.length })}
              {highlightedProductIds.length > 0 && (
                <span className="ml-2 text-primary">
                  ({highlightedProductIds.length} {t('pagination.highlighted')})
                </span>
              )}
            </span>
          </div>

          {/* Sort buttons */}
          <div className="flex items-center gap-2 mb-4" data-section="sorting">
            <span className="text-sm text-muted-foreground">{t('store.sort_label')}</span>
            <div className="flex gap-1">
              <button onClick={() => setSortBy('rating')} className={sortBtnClass(sortBy === 'rating')}>
                {t('store.sort_best')}
              </button>
              <button onClick={() => setSortBy('price_asc')} className={sortBtnClass(sortBy === 'price_asc')}>
                {t('store.sort_price_low')}
              </button>
              <button onClick={() => setSortBy('price_desc')} className={sortBtnClass(sortBy === 'price_desc')}>
                {t('store.sort_price_high')}
              </button>
            </div>
          </div>

          {/* Pagination - Top */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            previousLabel={t('pagination.previous')}
            nextLabel={t('pagination.next')}
            className="mb-6"
          />

          <ProductGrid
            products={paginatedProducts}
            loading={loading}
            highlightedIds={highlightedProductIds}
            onProductClick={setSelectedProduct}
          />

          {/* Pagination - Bottom */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            previousLabel={t('pagination.previous')}
            nextLabel={t('pagination.next')}
            className="mt-8"
          />
        </div>
      </section>

    </div>
  );
}
